import { Alert, Box, Button, ButtonBase, CircularProgress, Stack, Typography, alpha, useTheme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { SessionImageCandidateDTO } from '../../../../api/generated';
import { normalizeCaptureStartAt } from '../../../../api/resources/useOcInsightsEvents';
import { formatTime } from '../../../../utils';

interface SelfServiceImageSelectorProps {
  images: SessionImageCandidateDTO[];
  onSelect: (captureStartAt: string) => void;
  onSelectNone: () => void;
  processingCount?: number;
  selectedCaptureStartAt?: string | null;
}

export function SelfServiceImageSelector({
  images,
  onSelect,
  onSelectNone,
  processingCount = 0,
  selectedCaptureStartAt = null,
}: SelfServiceImageSelectorProps): ReactElement {
  const { t } = useTranslation();
  const theme = useTheme();

  const hasNothingToShow = images.length === 0 && processingCount === 0;
  if (hasNothingToShow) {
    return (
      <Stack flex={1} alignItems='center' justifyContent='center' gap={2} minHeight={200}>
        <CircularProgress />
        <Typography color='text.secondary'>{t('selfService.waitingForImages')}</Typography>
      </Stack>
    );
  }

  return (
    <Stack flex={1} minHeight={0} gap={2} overflow='hidden'>
      {processingCount > 0 && (
        <Alert
          severity='info'
          icon={<CircularProgress size='1.25rem' color='inherit' />}
          sx={{ alignItems: 'center', flexShrink: 0 }}
        >
          {t('selfService.imageSelector.processingHint', { count: processingCount })}
        </Alert>
      )}

      <Stack
        spacing={1.5}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          pr: 0.5,
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        {images.map((image, index) => {
          const isSelected = normalizeCaptureStartAt(image.captureStartAt) === selectedCaptureStartAt;
          return (
            <ButtonBase
              key={image.imageId}
              onClick={() => onSelect(image.captureStartAt)}
              focusRipple
              sx={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                borderRadius: 2,
                overflow: 'hidden',
                border: `2px solid ${isSelected ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.25)}`,
                backgroundColor: 'background.paper',
                boxShadow: isSelected ? 3 : 0,
                flexShrink: 0,
                transition: 'border-color 120ms, box-shadow 120ms',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '16 / 9',
                  backgroundColor: 'grey.900',
                }}
              >
                <Box
                  component='img'
                  src={image.imagePresignedUrl}
                  alt={image.licensePlate ?? `#${index + 1}`}
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
                {isSelected && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: alpha(theme.palette.success.main, 0.95),
                      color: 'success.contrastText',
                    }}
                  >
                    <CheckCircleIcon fontSize='small' />
                    <Typography variant='caption' sx={{ fontWeight: 600 }}>
                      {t('selfService.imageSelector.currentlySelected')}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Stack direction='row' alignItems='center' justifyContent='space-between' px={2} py={1.25} gap={2}>
                <Typography variant='subtitle1' component='div' noWrap>
                  {image.licensePlate || t('selfService.unknownLicensePlate')}
                </Typography>
                <Typography variant='body2' color='text.secondary' noWrap>
                  {formatTime(new Date(image.captureStartAt))}
                </Typography>
              </Stack>
            </ButtonBase>
          );
        })}

        {Array.from({ length: processingCount }).map((_, index) => (
          <Box
            key={`processing-${index}`}
            sx={{
              width: '100%',
              borderRadius: 2,
              border: `2px dashed ${theme.palette.divider}`,
              backgroundColor: 'grey.50',
              flexShrink: 0,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: '100%',
                aspectRatio: '16 / 9',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              <CircularProgress size='1.5rem' />
              <Typography variant='body2' color='text.secondary'>
                {t('selfService.imageSelector.processing')}
              </Typography>
            </Box>
          </Box>
        ))}
      </Stack>

      <Button variant='outlined' color='primary' fullWidth onClick={onSelectNone} sx={{ flexShrink: 0 }}>
        {t('selfService.imageSelector.noneCorrect')}
      </Button>
    </Stack>
  );
}
